import React from 'react';
import { FormData, FormStep } from '../../types';

interface ReviewFormProps {
  data: FormData;
  /** Called with a section identifier (not a numeric step) so the page
   *  can route to whichever wizard step hosts that section. Section names
   *  are stable across wizard restructures; numeric indices are not. */
  onEdit: (section: FormStep) => void;
}

interface SectionProps {
  label: string;
  section: FormStep;
  onEdit: (section: FormStep) => void;
  children: React.ReactNode;
}

const Section: React.FC<SectionProps> = ({ label, section, onEdit, children }) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      padding: 'var(--space-5) 0',
      borderBottom: '1px solid var(--line)',
      gap: 'var(--space-6)',
      flexWrap: 'wrap',
    }}
  >
    <div
      style={{
        fontFamily: 'var(--font-mono)',
        fontSize: '0.7rem',
        color: 'var(--ink-muted)',
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        minWidth: '130px',
        paddingTop: 4,
      }}
    >
      {label}
    </div>
    <div
      style={{
        flex: 1,
        minWidth: '240px',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: 'var(--space-3) var(--space-6)',
        fontSize: 'var(--text-sm)',
      }}
    >
      {children}
    </div>
    <button
      type="button"
      onClick={() => onEdit(section)}
      style={{
        background: 'transparent',
        border: 'none',
        padding: '4px 8px',
        borderRadius: 'var(--radius-sm)',
        fontFamily: 'var(--font-mono)',
        fontSize: '0.65rem',
        color: 'var(--accent)',
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        fontWeight: 500,
        cursor: 'pointer',
        transition: 'background-color var(--transition-fast)',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = 'var(--accent-soft)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = 'transparent';
      }}
    >
      Edit
    </button>
  </div>
);

interface FieldProps {
  label: string;
  value: string | undefined;
}

const Field: React.FC<FieldProps> = ({ label, value }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
    <span style={{ color: 'var(--ink-muted)', fontSize: 'var(--text-xs)' }}>{label}</span>
    <span
      style={{
        color: value ? 'var(--ink)' : 'var(--ink-faint)',
        fontWeight: 500,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {value || '—'}
    </span>
  </div>
);

interface ColorSwatchProps {
  label: string;
  color: string;
}

const ColorSwatch: React.FC<ColorSwatchProps> = ({ label, color }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
    <div
      style={{
        width: '26px',
        height: '26px',
        backgroundColor: color,
        border: '1px solid rgba(0, 0, 0, 0.1)',
        borderRadius: 'var(--radius-sm)',
        flexShrink: 0,
        boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.08)',
      }}
    />
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
      <span style={{ color: 'var(--ink-muted)', fontSize: 'var(--text-xs)' }}>{label}</span>
      <span
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 'var(--text-xs)',
          color: 'var(--ink)',
          textTransform: 'uppercase',
        }}
      >
        {color}
      </span>
    </div>
  </div>
);

export const ReviewForm: React.FC<ReviewFormProps> = React.memo(({ data, onEdit }) => {
  return (
    <div>
      <p style={{ color: 'var(--ink-muted)', fontSize: 'var(--text-sm)', marginBottom: 'var(--space-4)' }}>
        Everything looks good? One click below to start. Estimated build time: ~4 minutes.
      </p>

      <Section label="Business" section="business" onEdit={onEdit}>
        <Field label="Company" value={data.business.name} />
        <Field label="Legal entity" value={data.business.legalEntity} />
        <Field label="Registration #" value={data.business.registrationNumber} />
        <Field label="VAT #" value={data.business.vatNumber} />
        <Field label="Business type" value={data.business.businessType} />
        <Field label="Industry" value={data.business.industry} />
      </Section>

      <Section label="Location" section="location" onEdit={onEdit}>
        <Field label="Country" value={data.location.country} />
        <Field label="City" value={data.location.city} />
        <Field label="Street" value={data.location.street} />
        <Field label="Postal code" value={data.location.postalCode} />
        <Field label="Region" value={data.location.region} />
      </Section>

      <Section label="Website" section="website" onEdit={onEdit}>
        <Field label="Domain" value={data.website.domain} />
        <Field label="Language" value={data.website.language} />
        <Field label="Language code" value={data.website.languageCode} />
        <Field label="Currency" value={data.website.currency} />
        <Field label="Target region" value={data.website.targetRegion} />
      </Section>

      <Section label="Contact" section="contact" onEdit={onEdit}>
        <Field label="Primary email" value={data.contact.primaryEmail} />
        <Field label="Support email" value={data.contact.supportEmail} />
        <Field label="Legal email" value={data.contact.legalEmail} />
        <Field label="Privacy email" value={data.contact.privacyEmail} />
        <Field label="Phone" value={data.contact.phone} />
        <Field label="Business hours" value={data.contact.businessHours} />
      </Section>

      <Section label="Design" section="design" onEdit={onEdit}>
        <ColorSwatch label="Primary" color={data.design.primaryColor} />
        <ColorSwatch label="Secondary" color={data.design.secondaryColor} />
        <ColorSwatch label="Accent" color={data.design.accentColor} />
        <Field label="Font family" value={data.design.fontFamily} />
        <Field
          label="Corner radius"
          value={{
            none: 'None',
            sm: 'Small',
            md: 'Medium',
            lg: 'Large',
          }[data.design.borderRadius]}
        />
        <Field
          label="Style preset"
          value={{
            corporate: 'Corporate',
            'minimal-light': 'Minimal Light',
            gradient: 'Gradient',
            nature: 'Nature',
            luxury: 'Luxury',
            modern: 'Modern',
          }[data.design.stylePreset]}
        />
      </Section>
    </div>
  );
});

ReviewForm.displayName = 'ReviewForm';

export default ReviewForm;
